import React, { ElementType, Ref, forwardRef, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";

import classNames from "@/helpers/classNames";
import {
  Listbox,
  ListboxButtonProps,
  ListboxOptionProps,
  ListboxOptionsProps,
} from "@headlessui/react";
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";

import { Button } from "../Button";

export type DropdownItem<T = object> = {
  id: string;
  title: string;
  description?: string | JSX.Element;
  icon?: JSX.Element;
  value?: T;
  disabled?: boolean;
  badge?: React.ReactNode;
};

type Props<T> = {
  disabled?: boolean;
  options: DropdownItem<T>[];
  placeholder?: string;
  selected?: DropdownItem<T> | null;
  onSelect: (value: DropdownItem<T>) => void;
  allowClickDisabled?: true;
  className?: string;
  children?: (option: DropdownItem<T>) => React.ReactNode;
};

function ListboxDropdown<T>(props: Props<T>) {
  const [referenceElement, setReferenceElement] = useState<any>(null);
  const [popperElement, setPopperElement] = useState<any>(null);
  const { styles, attributes, forceUpdate } = usePopper(
    referenceElement,
    popperElement,
    {
      strategy: "absolute",
      placement: "bottom-start",
      modifiers: [
        { name: "preventOverflow", enabled: true },
        { name: "offset", options: { offset: [0, 10] } },
      ],
    },
  );

  const width = useMemo(
    () => referenceElement?.getBoundingClientRect().width || 0,
    [referenceElement],
  );

  return (
    <div ref={setReferenceElement} className={props.className}>
      <Listbox
        value={props.selected}
        onChange={props.onSelect}
        disabled={props.disabled}
      >
        <Listbox.Label className="sr-only">
          {props.placeholder ?? "Select an option"}
        </Listbox.Label>

        <ListboxButton className="h-auto" onClick={() => forceUpdate?.()}>
          {props.selected?.icon && (
            <div className="mr-2 flex size-4 flex-none items-center justify-center">
              {props.selected?.icon}
            </div>
          )}
          <span className="grow text-left text-sm">
            <div className={classNames(!props.selected && "opacity-50")}>
              {props.selected?.title ?? props.placeholder ?? "Select option"}
            </div>
            {props.selected?.description && (
              <div className="text-xs font-normal text-gray-500">
                {props.selected?.description}
              </div>
            )}
          </span>
          <ChevronsUpDownIcon className="w-5 text-gray-500" />
        </ListboxButton>

        {createPortal(
          <ListboxOptions
            ref={setPopperElement}
            style={{ ...styles.popper, width, zIndex: 60 }}
            {...attributes.popper}
          >
            {props.options.map((option) =>
              typeof props.children === "function" ? (
                props.children(option)
              ) : (
                <ListboxOption
                  key={option.id}
                  value={option}
                  disabled={option.disabled}
                >
                  {option.icon && (
                    <div className="flex-none">{option.icon}</div>
                  )}
                  <div className="grow truncate">
                    <p className="text-left">{option.title}</p>
                    {option.description && (
                      <p className="w-full text-left text-xs">
                        {option.description}
                      </p>
                    )}
                  </div>
                  {option.badge}
                </ListboxOption>
              ),
            )}
          </ListboxOptions>,
          document.body,
        )}
      </Listbox>
    </div>
  );
}

export default ListboxDropdown;

export const ListboxButton = forwardRef<
  HTMLButtonElement,
  ListboxButtonProps<typeof Button>
>((props, ref) => {
  return (
    <Listbox.Button
      ref={ref}
      as={Button}
      {...props}
      className={(state) =>
        classNames(
          "flex w-full items-center space-x-2 rounded-xs border bg-background px-4 py-2 focus:ring-2 focus:ring-primary focus:outline-hidden",
          state.disabled && "opacity-50",
          state.open && "ring-2 ring-primary",
          typeof props.className === "function"
            ? props.className(state)
            : props.className,
        )
      }
    />
  );
});

export const ListboxOptions = forwardRef<
  HTMLUListElement,
  ListboxOptionsProps<"ul">
>((props, ref) => {
  return (
    <Listbox.Options
      ref={ref}
      {...props}
      className={classNames(
        "absolute left-0 z-50 max-h-96 w-full origin-top-right overflow-auto rounded-xs border bg-popover shadow-lg focus:outline-hidden",
        props.className,
      )}
    />
  );
});

function ListboxOptionFn<TTag extends ElementType, TType>(
  props: ListboxOptionProps<TTag, TType>,
  ref: Ref<HTMLElement>,
) {
  const { children, ...restProps } = props as any;
  return (
    <Listbox.Option
      ref={ref}
      {...restProps}
      value={props.value}
      className={({ active, disabled }) => {
        return classNames(
          "flex w-full cursor-default items-center gap-x-4 px-4 py-2",
          {
            "bg-accent": active,
          },
          disabled && "opacity-40",
          (props as any).className,
        );
      }}
    >
      {({ active, selected, disabled }: any) => (
        <>
          {typeof children === "function"
            ? children({ active, selected, disabled })
            : children}
          {selected && (
            <CheckIcon className="ml-auto size-5 shrink-0" aria-hidden="true" />
          )}
        </>
      )}
    </Listbox.Option>
  );
}

export const ListboxOption = forwardRef(ListboxOptionFn) as <
  TTag extends ElementType,
  TType,
>(
  props: ListboxOptionProps<TTag, TType> & { ref?: Ref<HTMLElement> },
) => ReturnType<typeof ListboxOptionFn>;
